/**
 * src/lib/knowledge/areas.ts
 * The 15 fundamental inquiry areas of the Architecture of Knowledge curriculum,
 * together with structured pedagogical cross-links.
 */

export interface KnowledgeArea {
  id: string;
  n: number;
  title: string;
  summary: string;
  status: "open" | "later";
  pageId?: string;
  character: string;
  extraLink?: {
    title: string;
    route: string;
  };
}

export interface AreaLink {
  from: string;
  to: string;
  why: string;
}

export const AREAS: KnowledgeArea[] = [
  {
    id: "reality",
    n: 1,
    title: "Reality and Existence",
    summary: "Ontology and the foundational nature of being, substance, space-time, and whether physical matter exhausts what is real.",
    status: "later",
    character: "Metaphysical foundationalism investigating what fundamentally exists and what grounds physical cosmos.",
    extraLink: {
      title: "Explore Simulation Theory",
      route: "/SimulationTheoryPage",
    },
  },
  {
    id: "philosophy",
    n: 2,
    title: "Philosophy and Fundamental Questions",
    summary: "Philosophy as a systematic method of conceptual clarification, normative evaluation, and structured inquiry into first principles.",
    status: "open",
    pageId: "philosophy",
    character: "Expansive, contemplative, and foundational framing of the questions that precede empirical measurement.",
  },
  {
    id: "logic",
    n: 3,
    title: "Logic and Mathematical Reasoning",
    summary: "The formal architecture of inference, validity, soundness, propositional truth conditions, and deductive entailment.",
    status: "open",
    pageId: "logic",
    character: "Precise, algorithmic, and demonstrative analysis of truth-preserving symbolic structures.",
  },
  {
    id: "critical",
    n: 4,
    title: "Critical Thinking and Argument Analysis",
    summary: "Dissecting real-world claims into premises and conclusions, identifying implicit assumptions, and steelmanning rival positions.",
    status: "open",
    pageId: "critical",
    character: "Applied, diagnostic examination of everyday, scientific, and scholarly discourse.",
  },
  {
    id: "fallacies",
    n: 5,
    title: "Logical Fallacies and Misleading Reasoning",
    summary: "Identifying recurring patterns of flawed inference, cognitive deception, rhetorical sleight-of-hand, and pseudo-proofs.",
    status: "open",
    pageId: "fallacies",
    character: "Specimen-room taxonomy classifying structural vulnerabilities and conversational pitfalls in reasoning.",
  },
  {
    id: "epistemology",
    n: 6,
    title: "Epistemology and Theories of Knowledge",
    summary: "Analyzing the definition of knowledge, conditions of justification, responses to radical skepticism, and epistemic virtues.",
    status: "open",
    pageId: "epistemology",
    character: "Cold, analytical investigation into the epistemic criteria separating genuine knowledge from mere true belief.",
  },
  {
    id: "scientific-reasoning",
    n: 7,
    title: "Scientific Reasoning and Philosophy of Science",
    summary: "How hypotheses are formed, tested, falsified, and integrated into overarching predictive paradigms and models.",
    status: "later",
    character: "Empirical, inductive, and abductive examination of the mechanics of discovery and scientific change.",
  },
  {
    id: "cognitive-science",
    n: 8,
    title: "Cognitive Science and Human Judgment",
    summary: "Heuristics, systemic cognitive biases, perceptual illusions, and evolutionary constraints shaping human rationality.",
    status: "later",
    character: "Empirical cognitive modeling revealing where human intuition systematically diverges from normative logic.",
  },
  {
    id: "probability",
    n: 9,
    title: "Probability, Statistics and Uncertainty",
    summary: "Bayesian updating, frequentist confidence, base rates, confounding variables, and quantifying degrees of belief.",
    status: "later",
    character: "Quantitative calculus for navigating incomplete information and avoiding stochastic fallacies.",
  },
  {
    id: "language",
    n: 10,
    title: "Language, Meaning and Interpretation",
    summary: "Semantics, pragmatics, speech acts, ambiguity, linguistic framing, and how syntax and metaphor shape thought.",
    status: "later",
    character: "Linguistic and hermeneutic dissection of how symbols encode, distort, or clarify meaning.",
  },
  {
    id: "ethics",
    n: 11,
    title: "Ethics, Morality and Responsibility",
    summary: "Normative ethical frameworks, meta-ethical realism, moral obligation, agency, and responsibility under uncertainty.",
    status: "later",
    character: "Principled normative evaluation of what agents ought to value, choose, and hold themselves accountable for.",
  },
  {
    id: "consciousness",
    n: 12,
    title: "Consciousness, Identity and the Mind",
    summary: "The hard problem of subjective experience, personal identity through time, mental causation, and computational functionalism.",
    status: "later",
    character: "Phenomenological and neurocomputational inquiry into the first-person perspective in a physical universe.",
    extraLink: {
      title: "Explore Simulation & Mind",
      route: "/SimulationTheoryPage",
    },
  },
  {
    id: "decision-making",
    n: 13,
    title: "Decision-Making and Rational Action",
    summary: "Decision theory, game theory, expected utility maximization, risk assessment, and strategic cooperation.",
    status: "later",
    character: "Action-oriented optimization of agency under strategic interdependence and resource constraints.",
  },
  {
    id: "skepticism-misinformation",
    n: 14,
    title: "Skepticism, Misinformation and Source Evaluation",
    summary: "Evaluating institutional authority, detecting disinformation networks, corroborating primary evidence, and calibrating epistemic trust.",
    status: "later",
    character: "Defensive epistemic hygiene designed to audit credibility and combat systemic information manipulation.",
  },
  {
    id: "intellectual-humility",
    n: 15,
    title: "Intellectual Humility and the Limits of Knowledge",
    summary: "Recognizing one's own cognitive blindspots, embracing fallibilism, and maintaining open-ended curiosity before uncharted complexity.",
    status: "later",
    character: "Reflective metacognition balancing confident conviction with awareness of intrinsic human finitude.",
  },
];

export const AREA_LINKS: AreaLink[] = [
  {
    from: "philosophy",
    to: "epistemology",
    why: "Epistemology emerged as a core branch of philosophy to define what separates justified understanding from arbitrary conviction.",
  },
  {
    from: "logic",
    to: "critical",
    why: "Critical thinking takes the formal rules of validity from logic and adapts them to diagnose informal everyday arguments.",
  },
  {
    from: "logic",
    to: "fallacies",
    why: "Formal fallacies represent direct violations of deductive rules such as affirming the consequent or denying the antecedent.",
  },
  {
    from: "critical",
    to: "fallacies",
    why: "Detecting rhetorical distractions and informal fallacies is an essential diagnostic toolkit for evaluating argumentation.",
  },
  {
    from: "epistemology",
    to: "skepticism-misinformation",
    why: "Epistemic theories of justification provide the normative standards needed to evaluate reliable testimony versus fabricated misinformation.",
  },
  {
    from: "reality",
    to: "epistemology",
    why: "How the world fundamentally is (metaphysics) constrains how conscious observers can acquire justified beliefs about it (epistemology).",
  },
  {
    from: "reality",
    to: "skepticism-misinformation",
    why: "The modern simulation hypothesis is an updated computational form of classical Cartesian dream and evil-demon skepticism.",
  },
  {
    from: "fallacies",
    to: "probability",
    why: "Many persuasive statistical fallacies, like the prosecutor's fallacy or gambler's fallacy, stem from ignoring foundational base rates.",
  },
  {
    from: "cognitive-science",
    to: "fallacies",
    why: "Cognitive biases explain why the human brain reliably stumbles into predictable fallacies like confirmation bias and availability heuristics.",
  },
  {
    from: "scientific-reasoning",
    to: "probability",
    why: "Modern empirical science evaluates competing hypotheses through statistical significance, Bayesian priors, and confidence intervals.",
  },
  {
    from: "scientific-reasoning",
    to: "epistemology",
    why: "The demarcation problem in philosophy of science questions how scientific justification fundamentally differs from ungrounded dogma.",
  },
  {
    from: "critical",
    to: "language",
    why: "Equivocation, vague definitions, and emotionally loaded phrasing frequently obscure argument premises until clarified linguistically.",
  },
  {
    from: "logic",
    to: "scientific-reasoning",
    why: "Inductive and abductive inference extend deductive logic to permit tentative generalizations from finite empirical data.",
  },
  {
    from: "decision-making",
    to: "probability",
    why: "Rational decision-making requires calculating expected value by weighting potential payoffs against their probabilistic likelihood.",
  },
  {
    from: "decision-making",
    to: "ethics",
    why: "Evaluating the desirability of outcomes demands normative ethical principles to decide which tradeoffs are permissible.",
  },
  {
    from: "consciousness",
    to: "reality",
    why: "Whether subjective qualia can be reduced to physical matter or constitute a distinct aspect of reality remains unresolved in metaphysics.",
  },
  {
    from: "consciousness",
    to: "epistemology",
    why: "Direct first-person experience provides foundational certainty (cogito) yet poses unique challenges for third-person empirical verification.",
  },
  {
    from: "intellectual-humility",
    to: "epistemology",
    why: "Fallibilism maintains that empirical claims remain open to future revision, grounding intellectual humility in rigorous epistemology.",
  },
  {
    from: "intellectual-humility",
    to: "critical",
    why: "Steelmanning opposing viewpoints requires the humility to acknowledge that alternative perspectives may hold valid premises.",
  },
  {
    from: "ethics",
    to: "critical",
    why: "Disentangling descriptive claims from normative obligations is essential to avoid the naturalistic fallacy in ethical debates.",
  },
  {
    from: "language",
    to: "philosophy",
    why: "The linguistic turn in twentieth-century philosophy demonstrated that many conceptual paradoxes arise from linguistic confusions.",
  },
  {
    from: "cognitive-science",
    to: "critical",
    why: "Awareness of instinctive cognitive heuristics enables thinkers to engage reflective System 2 analysis when evaluating complex claims.",
  },
  {
    from: "probability",
    to: "skepticism-misinformation",
    why: "Understanding confounding variables and cherry-picked statistical samples protects against misleading scientific disinformation.",
  },
  {
    from: "intellectual-humility",
    to: "scientific-reasoning",
    why: "The history of science repeatedly demonstrates that long-standing consensus models are subject to revolutionary paradigm shifts.",
  },
];
